import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';

export type NotificationCardProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  actionLabel?: React.ReactNode;
  accentColor?: string;
  accentSoftColor?: string;
  sx?: SystemStyleObject<Theme>;
};

const CARD_SX: SystemStyleObject<Theme> = {
  width: '100%',
  maxWidth: '334px',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'border.separator',
  backgroundColor: 'background.paper',
  textAlign: 'left',
  px: 1.5,
  py: 1.25,
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
  '&:hover': {
    backgroundColor: 'common.menuItemActive',
    borderColor: 'primary.main',
  },
};

const CONTENT_SX = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.25,
  width: '100%',
} as const;

const ICON_WRAP_SX = {
  width: 32,
  height: 32,
  borderRadius: 999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mt: 0.125,
} as const;

const BODY_SX = {
  minWidth: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
} as const;

const FOOTER_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  mt: 0.25,
} as const;

const NotificationCard = ({
  title,
  description,
  icon,
  onClick,
  actionLabel = 'Open',
  accentColor = 'primary.main',
  accentSoftColor = 'background.primary',
  sx,
}: NotificationCardProps) => {
  const content = (
    <Box sx={CONTENT_SX}>
      {icon ? <Box sx={{ ...ICON_WRAP_SX, color: accentColor, backgroundColor: accentSoftColor }}>{icon}</Box> : null}
      <Box sx={BODY_SX}>
        <Typography variant="subtitle2" color="textPrimary">
          {title}
        </Typography>

        {description ? (
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        ) : null}

        {onClick ? (
          <Box sx={FOOTER_SX}>
            <Typography variant="subtitle2" color="primary">
              {actionLabel}
            </Typography>

            <ChevronRightIcon sx={{ fontSize: 16, mt: 0.3 }} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  if (!onClick) {
    return <Box sx={{ ...CARD_SX, ...sx }}>{content}</Box>;
  }

  return (
    <ButtonBase onClick={onClick} sx={{ ...CARD_SX, ...sx }}>
      {content}
    </ButtonBase>
  );
};

export default NotificationCard;
